import type { AddBaggageToBookingInStateBody } from "@repo/ui/api/addBaggageToBookingInState";
import type { SsrAvailability, SsrByJourney, SsrDetail } from "@repo/ui/types/ssr";

import type { BaggageCart } from "@repo/store/slices";

// Internal type definition for a flattened bag item with journey context
interface FlattenedBagWithJourney {
  ssrCode: string;
  quantity: number;
  passengerKey: string;
  journeyIndex: number; // 0 for outbound, 1 for inbound
}

/**
 * Builds the payload required to add selected baggage to the booking state.
 * It flattens the selected bags from the cart and finds the necessary SSR details
 * (name and ssrKey) from the SSR availability data, optimizing the lookup
 * by using the journey index.
 *
 * @param ssrAvailability - The full SSR availability structure containing SSR keys and names.
 * @param baggageCart - The selected baggage items for outbound and inbound journeys.
 * @param currency - The currency code for the transaction (e.g., 'CLP', 'USD').
 * @param isUserStaff - Flag indicating if the user performing the action is a staff member.
 * @returns The structured payload for the API.
 */
export default function buildAddBaggageInBookingPayload(
  ssrAvailability: SsrAvailability,
  baggageCart: { outbound?: BaggageCart; inbound?: BaggageCart },
  currency: string,
  isUserStaff: boolean,
): AddBaggageToBookingInStateBody {
  // 1. Flatten all selected bags across both outbound (index 0) and inbound (index 1) journeys,
  // recording the journeyIndex for later lookup.
  const selectedBags: FlattenedBagWithJourney[] = [];
  const carts: (BaggageCart | undefined)[] = [baggageCart.outbound, baggageCart.inbound];

  for (let journeyIndex = 0; journeyIndex < carts.length; journeyIndex++) {
    const cart = carts[journeyIndex];
    if (!cart) continue;

    for (const passenger of cart.passengers) {
      for (const bag of passenger.bags) {
        selectedBags.push({
          ssrCode: bag.ssrCode,
          quantity: bag.quantity,
          passengerKey: passenger.passengerKey,
          journeyIndex: journeyIndex, // Capture the journey index here
        });
      }
    }
  }

  // 2. Map the flattened bags to the final AddBaggageToBookingInStateBody['baggages'] structure,
  // using the pre-calculated journeyIndex for direct lookup.
  const finalBaggages: AddBaggageToBookingInStateBody["baggages"] = selectedBags.map((bag) => {
    let name = "";
    let ssrKey = "";

    // Direct access to the correct journey SSR using the stored journeyIndex (optimization)
    const journeySsr = ssrAvailability?.journeySsrs?.[bag.journeyIndex] as SsrByJourney;

    if (journeySsr && journeySsr.ssrs && Array.isArray(journeySsr.ssrs)) {
      // Find the SSR definition that matches the selected bag's SSR code within that journey
      const matchingSsr = (journeySsr.ssrs as SsrDetail[]).find(
        (ssr) => ssr.ssrCode === bag.ssrCode,
      );

      if (matchingSsr) {
        // 1. Extract the name
        name = matchingSsr.name ?? "";

        // 2. Look up the passenger-specific availability to get the unique ssrKey
        const passengerAvailability = matchingSsr.passengersAvailability?.[bag.passengerKey];

        if (passengerAvailability) {
          ssrKey = passengerAvailability.ssrKey ?? "";
          // Note: No need for a break here as we are already in the map function.
        }
      }
    }

    // Defensive check: If name or ssrKey is missing, this indicates an issue in the input data
    if (!ssrKey || !name) {
      console.warn(
        `Could not find SSR details for ssrCode: ${bag.ssrCode} and passengerKey: ${bag.passengerKey} (Journey Index: ${bag.journeyIndex}). Using placeholders.`,
      );
    }

    return {
      name: name || `Missing Name (${bag.ssrCode})`, // Provide a fallback name
      quantity: bag.quantity,
      ssrCode: bag.ssrCode,
      ssrKey: ssrKey || "MISSING_SSR_KEY", // Provide a fallback key
      passengerKey: bag.passengerKey,
    };
  });

  // 3. Assemble the final payload object.
  return {
    baggages: finalBaggages,
    currency,
    isUserStaff,
  };
}
